import Material from '../materials/Material.js';

/**
 * DungeonTheme - Material palettes for different dungeon styles.
 * Each theme defines factory functions (not shared instances) for materials.
 * Vegetation uses Impostor sprite textures from public/textures/vegetation/.
 */
/**
 * Vegetation array order matters for placement logic:
 *   [0] Primary clump type — most common, forms large patches
 *   [1] Secondary clump type — similar look, patches alongside primary
 *   [2] Wall-edge type — low/ground-hugging, grows along walls
 *   [3] Accent type — sparse scatter, adds visual interest
 *
 * Each theme's vegetation uses a cohesive color palette.
 * Scale 1.0 = one cuboid tall. Variance (0.75–1.5) applied per instance.
 */
const THEMES = {
  stone_crypt: {
    name: 'Stone Crypt',
    floor: {
      top: () => Material.texture('textures/cave/dungeon_floor_1.webp'),
      sides: () => Material.texture('textures/stone/granite_1.webp')
    },
    wall: {
      front: () => Material.texture('textures/brick/dungeon_brick_1.webp'),
      sides: () => Material.texture('textures/brick/dungeon_brick_2.webp'),
      top: () => Material.texture('textures/stone/slate_1.webp')
    },
    pillar: () => Material.texture('textures/stone/granite_2.webp'),
    chest: () => Material.texture('textures/wood/oak_1.webp'),
    portal: () => Material.special('neon', '#4400ff', 0.6),
    sunIntensity: 0.5,
    // Palette: dark muted greens — dank underground growth
    vegetation: [
      { texture: 'textures/vegetation/regional_specialty/fern_volcanic_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_fescue_dark_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/moss_arctic_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/lichen_tundra_256.webp', scale: 1.0 }
    ]
  },

  cave: {
    name: 'Natural Cave',
    floor: {
      top: () => Material.texture('textures/cave/wet_stone_1.webp'),
      sides: () => Material.texture('textures/cave/wet_stone_2.webp')
    },
    wall: {
      front: () => Material.texture('textures/cave/carved_tunnel_1.webp'),
      sides: () => Material.texture('textures/cave/stalactite_1.webp'),
      top: () => Material.texture('textures/cave/wet_stone_1.webp')
    },
    pillar: () => Material.texture('textures/cave/carved_tunnel_2.webp'),
    chest: () => Material.texture('textures/wood/walnut_1.webp'),
    portal: () => Material.special('neon', '#00ff88', 0.6),
    sunIntensity: 0.4,
    // Palette: lush tropical greens — dripping wet cave life
    vegetation: [
      { texture: 'textures/vegetation/regional_specialty/fern_jungle_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/fern_volcanic_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/moss_arctic_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_tropical_broad_256.webp', scale: 1.0 }
    ]
  },

  ruins: {
    name: 'Ancient Ruins',
    floor: {
      top: () => Material.texture('textures/floor/flagstone_1.webp'),
      sides: () => Material.texture('textures/stone/limestone_1.webp')
    },
    wall: {
      front: () => Material.texture('textures/stone/sandstone_1.webp'),
      sides: () => Material.texture('textures/stone/sandstone_2.webp'),
      top: () => Material.texture('textures/stone/limestone_2.webp')
    },
    pillar: () => Material.texture('textures/stone/limestone_1.webp'),
    chest: () => Material.texture('textures/wood/pine_1.webp'),
    portal: () => Material.special('neon', '#ffaa00', 0.6),
    sunIntensity: 0.6,
    // Palette: warm golden overgrowth — nature reclaiming stone
    vegetation: [
      { texture: 'textures/vegetation/basic_grasses/grass_tall_wheat_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_mixed_meadow_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_dried_autumn_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/wildflowers/flower_wildflower_mixed_256.webp', scale: 1.0 }
    ]
  },

  fire_temple: {
    name: 'Fire Temple',
    floor: {
      top: () => Material.texture('textures/stone/basalt_1.webp'),
      sides: () => Material.texture('textures/stone/basalt_2.webp')
    },
    wall: {
      front: () => Material.texture('textures/brick/dungeon_brick_2.webp'),
      sides: () => Material.texture('textures/brick/dungeon_brick_1.webp'),
      top: () => Material.texture('textures/metal/wrought_iron_1.webp')
    },
    pillar: () => Material.texture('textures/metal/bronze_1.webp'),
    chest: () => Material.texture('textures/metal/rusted_iron_1.webp'),
    portal: () => Material.special('neon', '#ff4400', 0.8),
    sunIntensity: 0.3,
    // Palette: scorched golds and oranges — volcanic heat survivors
    vegetation: [
      { texture: 'textures/vegetation/basic_grasses/grass_savanna_golden_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_dried_autumn_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/fern_volcanic_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/succulent_desert_256.webp', scale: 1.0 }
    ]
  },

  ice_dungeon: {
    name: 'Ice Dungeon',
    floor: {
      top: () => Material.texture('textures/water/ice_1.webp'),
      sides: () => Material.texture('textures/water/ice_2.webp')
    },
    wall: {
      front: () => Material.texture('textures/stone/granite_1.webp'),
      sides: () => Material.texture('textures/stone/granite_2.webp'),
      top: () => Material.texture('textures/water/ice_1.webp')
    },
    pillar: () => Material.texture('textures/water/ice_2.webp'),
    chest: () => Material.texture('textures/wood/pine_2.webp'),
    portal: () => Material.special('glass', '#aaddff', 0.6),
    sunIntensity: 0.7,
    // Palette: cold blue-grays — frost-hardened sparse growth
    vegetation: [
      { texture: 'textures/vegetation/basic_grasses/grass_alpine_hardy_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_sea_blue_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/lichen_tundra_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/regional_specialty/moss_arctic_256.webp', scale: 1.0 }
    ]
  },

  arcane_library: {
    name: 'Arcane Library',
    floor: {
      top: () => Material.texture('textures/floor/parquet_1.webp'),
      sides: () => Material.texture('textures/wood/oak_1.webp')
    },
    wall: {
      front: () => Material.texture('textures/wood/walnut_1.webp'),
      sides: () => Material.texture('textures/wood/oak_2.webp'),
      top: () => Material.texture('textures/metal/bronze_1.webp')
    },
    pillar: () => Material.texture('textures/wood/walnut_2.webp'),
    chest: () => Material.texture('textures/wood/walnut_1.webp'),
    portal: () => Material.special('neon', '#aa00ff', 0.7),
    sunIntensity: 0.5,
    // Palette: mystical purples and blues — enchanted flora
    vegetation: [
      { texture: 'textures/vegetation/wildflowers/flower_lavender_purple_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/wildflowers/flower_thistle_purple_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/basic_grasses/grass_ornamental_wispy_256.webp', scale: 1.0 },
      { texture: 'textures/vegetation/wildflowers/flower_cornflower_blue_256.webp', scale: 1.0 }
    ]
  }
};

export default class DungeonTheme {
  static getTheme(name) {
    return THEMES[name] || THEMES.stone_crypt;
  }

  static getThemeNames() {
    return Object.keys(THEMES);
  }

  static getRandomTheme() {
    const names = Object.keys(THEMES);
    return THEMES[names[Math.floor(Math.random() * names.length)]];
  }

  /**
   * Build a full materials object for a floor cuboid.
   */
  static buildFloorMaterials(theme) {
    return {
      top: theme.floor.top(),
      bottom: theme.floor.sides(),
      front: theme.floor.sides(),
      back: theme.floor.sides(),
      left: theme.floor.sides(),
      right: theme.floor.sides()
    };
  }

  /**
   * Build a full materials object for a wall cuboid.
   */
  static buildWallMaterials(theme) {
    return {
      top: theme.wall.top(),
      bottom: theme.wall.sides(),
      front: theme.wall.front(),
      back: theme.wall.front(),
      left: theme.wall.sides(),
      right: theme.wall.sides()
    };
  }

  /**
   * Build materials for an exit portal cuboid.
   */
  static buildPortalMaterials(theme) {
    const mat = theme.portal;
    return {
      top: mat(), bottom: mat(),
      front: mat(), back: mat(),
      left: mat(), right: mat()
    };
  }

  /**
   * Build materials for a back portal cuboid (distinct blue glow).
   * Used in level 2+ start rooms to go back to the previous level.
   */
  static buildBackPortalMaterials() {
    const mat = () => Material.special('neon', '#0088ff', 0.6);
    return {
      top: mat(), bottom: mat(),
      front: mat(), back: mat(),
      left: mat(), right: mat()
    };
  }

  /**
   * Get vegetation impostor config array for a theme.
   * @returns {Array<{texture: string, scale: number}>}
   */
  static getVegetation(theme) {
    return theme.vegetation || [];
  }
}
